import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { tecnicos, visitas as visitasApi, clusters, tipoOs, escala } from '../lib/api.js';
import { formatHoraSC, hojeSC } from '../lib/tempo.js';
import KPICard from '../components/KPICard.jsx';
import Avatar from '../components/Avatar.jsx';
import DatalakeStatus from '../components/DatalakeStatus.jsx';
import { osLabel } from '../lib/visita.js';
import { classificarVisita, COR_STATUS_VISUAL, corRealizado } from '../lib/statusVisual.js';
import LegendaStatusVisita from '../components/LegendaStatusVisita.jsx';
import BuscaOS, { visitaCasaBusca, tecnicoCasaBusca } from '../components/BuscaOS.jsx';

const HORA_INICIO = 6;
const HORA_FIM    = 22;
const TOTAL_MIN   = (HORA_FIM - HORA_INICIO) * 60;
const PX_POR_MIN  = 1.6;
const LARGURA_TEC = 200;
const ALT_LANE    = 38;   // altura de uma pista (uma visita)
const GAP_LANES   = 2;    // gap entre pistas quando há sobreposição
const PADDING_V   = 6;    // padding vertical da linha (top + bottom)
const ALT_LINHA_MIN = 56; // altura mínima da linha do técnico

// Calcula altura da linha baseado no nº de lanes (sobreposições)
function alturaLinha(totalLanes) {
  return Math.max(ALT_LINHA_MIN, totalLanes * ALT_LANE + (totalLanes - 1) * GAP_LANES + PADDING_V * 2);
}
const COR_NOW     = '#dc2626';

const COR_UNIDADE = {
  Matriz:       '#1e6fc8',
  Serra:        '#16a34a',
  Praia:        '#ea580c',
  'Ilha Sul':   '#9333ea',
  'Ilha Leste': '#dc2626',
  '(sem unidade)': '#9ca3af',
};

const COR_SETOR = {
  suporte:        { cor: '#1e6fc8', label: '🔧 Suporte técnico' },
  ativacao:       { cor: '#16a34a', label: '🆕 Ativação' },
  recolhimento:   { cor: '#ea580c', label: '📥 Recolhimento' },
  infraestrutura: { cor: '#9333ea', label: '🏗️ Infraestrutura' },
  sem_dados:      { cor: '#9ca3af', label: '❓ Sem histórico' },
};

// COR_STATUS antigo removido — agora usamos COR_STATUS_VISUAL (lib/statusVisual.js)
// pra ter consistência com o mapa do Dashboard. Status visual considera:
// agendada / proxima_2h / em_andamento_no_horario / em_andamento_atrasada /
// atrasada / concluida / cancelada / nao_compareceu.

const COR_EXEC = '#22c55e';

function horaSCparaMin(iso) {
  if (!iso) return null;
  const dt = new Date(iso);
  const hhmm = dt.toLocaleString('en-GB', {
    timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit', hour12: false,
  });
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

function tipoOsCurto(s) {
  if (!s) return '';
  return s.replace(/^\(Suporte\)\s*-?\s*/, '').replace(/^\(Operação\)\s*/, '').replace(/^\(Infra de Rede\)\s*-?\s*/, '').trim();
}

function minToPx(min) {
  return (min - HORA_INICIO * 60) * PX_POR_MIN;
}

function nowMinSC() {
  const agora = new Date();
  const hhmm = agora.toLocaleString('en-GB', {
    timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit', hour12: false,
  });
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

// Aloca cada visita em uma "lane" (pista vertical) pra evitar sobreposição.
// Visitas que se sobrepõem no tempo vão pra lanes diferentes.
// Considera tanto a janela prevista quanto a execução real (se já houve).
function calcularLanes(visitas) {
  // Ordena por início (previsto, ou efetivo se já começou)
  const ordenadas = [...visitas].sort((a, b) => {
    const ai = horaSCparaMin(a.janela_inicio) ?? horaSCparaMin(a.dt_inicio_atendimento) ?? 0;
    const bi = horaSCparaMin(b.janela_inicio) ?? horaSCparaMin(b.dt_inicio_atendimento) ?? 0;
    return ai - bi;
  });

  // laneFimMin[i] = horário em que a lane i fica livre (em minutos do dia)
  const laneFimMin = [];
  const laneByVisitaId = new Map();

  for (const v of ordenadas) {
    const iniPrev = horaSCparaMin(v.janela_inicio);
    const fimPrev = horaSCparaMin(v.janela_fim);
    const iniReal = horaSCparaMin(v.dt_inicio_atendimento);
    const fimReal = horaSCparaMin(v.dt_fechamento);

    // Janela "ocupada" da visita = união de [previsto] ∪ [realizado] (se houver)
    let ini = iniPrev, fim = fimPrev;
    if (iniReal != null) ini = ini == null ? iniReal : Math.min(ini, iniReal);
    if (fimReal != null) fim = fim == null ? fimReal : Math.max(fim, fimReal);
    if (ini == null || fim == null) {
      laneByVisitaId.set(v.id, 0);
      continue;
    }

    // Acha primeira lane onde laneFimMin[i] <= ini (livre)
    let idx = laneFimMin.findIndex(f => f <= ini);
    if (idx === -1) {
      idx = laneFimMin.length;
      laneFimMin.push(fim);
    } else {
      laneFimMin[idx] = fim;
    }
    laneByVisitaId.set(v.id, idx);
  }

  return { laneByVisitaId, totalLanes: Math.max(1, laneFimMin.length) };
}

function dataSCcompare() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
}

export default function AgendaPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(hojeSC());
  const [dataInput, setDataInput] = useState(hojeSC());
  const [tecs, setTecs] = useState([]);
  const [visitas, setVisitas] = useState([]);
  const [clustersList, setClustersList] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [setoresPorTec, setSetoresPorTec] = useState({}); // { tecnico_id: { setor_primario, pct_suporte, pct_ativacao, ... } }
  const [erro, setErro] = useState(null);
  const [carregando, setCarregando] = useState(true);

  // Filtros
  const [agruparPor, setAgruparPor] = useState('unidade'); // 'unidade' | 'setor'
  const [filtroUnidade, setFiltroUnidade] = useState('todas');
  const [filtroSetor, setFiltroSetor] = useState('todos');
  const [filtroSkills, setFiltroSkills] = useState(new Set());
  // [removido] busca de técnico foi unificada em buscaOS — ver BuscaOS.jsx
  const [modoEscala, setModoEscala] = useState(true); // mostrar só técnicos na escala do dia
  const [escalaDia, setEscalaDia] = useState([]); // dados da escala do dia (jornada + overrides)
  const [feriadosNaData, setFeriadosNaData] = useState(null);
  const [filtroStatusVisual, setFiltroStatusVisual] = useState(null);
  const [buscaOS, setBuscaOS] = useState('');  // filtra blocos da agenda por status visual // feriado(s) do dia
  const [modalDisponibilidade, setModalDisponibilidade] = useState(null); // { tecnico, escala } ou null
  const [gruposColapsados, setGruposColapsados] = useState(new Set());

  const [agoraMin, setAgoraMin] = useState(nowMinSC());
  useEffect(() => {
    const t = setInterval(() => setAgoraMin(nowMinSC()), 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => { carregarTudo(); }, []);
  useEffect(() => { carregarVisitas(); }, [data]);

  async function carregarTudo() {
    setCarregando(true);
    try {
      const [t, c, top, sp] = await Promise.all([
        tecnicos.list({ ativo: 'true', limit: 200 }),
        clusters.list(),
        tipoOs.list(),
        tecnicos.setores(),
      ]);
      setTecs(t.data || []);
      setClustersList(c.data || []);
      setTipos((top.data || []).filter(x => x.ativo_para_agendamento));
      const mapaSetor = {};
      for (const r of (sp.data || [])) mapaSetor[String(r.tecnico_id)] = r;
      setSetoresPorTec(mapaSetor);
      await carregarVisitas();
    } catch (e) {
      setErro(e.uiMessage || 'Erro ao carregar');
    } finally { setCarregando(false); }
  }

  async function carregarVisitas() {
    try {
      const [v, e] = await Promise.all([
        visitasApi.list({ data, limit: 500 }),
        escala.do_dia(data),
      ]);
      setVisitas(v.data || []);
      setEscalaDia(e.data || []);
      setFeriadosNaData(e.feriado || null);
      setErro(null);
    } catch (err) {
      setErro(err.uiMessage || 'Erro ao carregar visitas');
    }
  }

  function aplicarData() {
    if (dataInput && dataInput !== data) setData(dataInput);
    else carregarVisitas();
  }

  const visitasPorTecnico = useMemo(() => {
    const m = {};
    for (const v of visitas) {
      if (!v.tecnico_id) continue;
      (m[String(v.tecnico_id)] = m[String(v.tecnico_id)] || []).push(v);
    }
    return m;
  }, [visitas]);

  const escalaPorTec = useMemo(() => {
    const m = {};
    for (const e of escalaDia) m[String(e.tecnico_id)] = e;
    return m;
  }, [escalaDia]);

  const tecsComSkillSelecionada = useMemo(() => {
    if (filtroSkills.size === 0) return null;
    const nomesEscolhidos = new Set();
    for (const id of filtroSkills) {
      const t = tipos.find(x => x.id === id);
      if (t) nomesEscolhidos.add(t.tipo);
    }
    return new Set(
      tecs.filter(t => (t.especializacoes || []).some(e => nomesEscolhidos.has(e))).map(t => String(t.id))
    );
  }, [filtroSkills, tipos, tecs]);

  const tecsFiltrados = useMemo(() => {
    let lista = tecs;
    if (tecsComSkillSelecionada) {
      lista = lista.filter(t => tecsComSkillSelecionada.has(String(t.id)));
    }
    if (filtroUnidade !== 'todas') {
      lista = lista.filter(t => t.unidade_negocio === filtroUnidade || t.atende_todas_unidades);
    }
    if (filtroSetor !== 'todos') {
      // "Contém" — inclui técnicos que fazem >= 15% naquele setor (não só primário)
      lista = lista.filter(t => {
        const info = setoresPorTec[String(t.id)];
        if (!info) return filtroSetor === 'sem_dados';
        return (info.setores_significativos || []).includes(filtroSetor);
      });
    }
    // Esconder técnicos de infra quando há filtro de setor específico (exceto 'todos' ou 'infraestrutura')
    if (filtroSetor !== 'todos' && filtroSetor !== 'infraestrutura') {
      lista = lista.filter(t => {
        const info = setoresPorTec[String(t.id)];
        return !info || info.setor_primario !== 'infraestrutura';
      });
    }
    // Busca unificada (buscaOS): mostra técnicos cujo NOME bate com o termo
    // OU que tenham pelo menos uma visita matching com o termo.
    // Quando vazio, deixa passar tudo (filtros de unidade/setor agem normalmente).
    if (buscaOS.trim()) {
      const termo = buscaOS;
      lista = lista.filter(x => {
        if (tecnicoCasaBusca(x, termo)) return true;
        const visitasDoTec = visitasPorTecnico[String(x.id)] || [];
        return visitasDoTec.some(v => visitaCasaBusca(v, termo));
      });
    }
    if (modoEscala) {
      // Mostra técnicos NA ESCALA do dia
      // Em feriado: mostra só os de plantão (esconde folga)
      // Em dia normal: na_escala_padrao + overrides
      lista = lista.filter(t => {
        const e = escalaPorTec[String(t.id)];
        if (!e) return false;
        if (e.status === 'feriado_folga') return false; // esconde em modo escala
        if (e.status === 'plantao_feriado') return true;
        return e.na_escala_padrao || ['ausente_dia','jornada_reduzida','atrasado'].includes(e.status);
      });
    }
    return lista;
  }, [tecs, buscaOS, filtroUnidade, filtroSetor, tecsComSkillSelecionada, visitasPorTecnico, modoEscala, escalaPorTec, setoresPorTec]);

  // Agrupamento
  // Resumo da busca de OS (encontradas vs total)
  const resumoBuscaOS = useMemo(() => {
    if (!buscaOS || !buscaOS.trim()) return null;
    const total = visitas.length;
    const encontradas = visitas.filter(v => visitaCasaBusca(v, buscaOS)).length;
    return { total, encontradas };
  }, [visitas, buscaOS]);

  // Contagens por status visual pra alimentar a legenda
  const resumoStatusVisual = useMemo(() => {
    const r = { agendadas: 0, proxima_2h: 0, em_andamento_no_horario: 0, em_andamento_atrasada: 0, atrasadas: 0, concluidas: 0 };
    const agora = new Date();
    for (const v of visitas) {
      const sv = classificarVisita(v, agora);
      if (sv === 'agendada')                 r.agendadas++;
      else if (sv === 'proxima_2h')          r.proxima_2h++;
      else if (sv === 'em_andamento_no_horario') r.em_andamento_no_horario++;
      else if (sv === 'em_andamento_atrasada')   r.em_andamento_atrasada++;
      else if (sv === 'atrasada')            r.atrasadas++;
      else if (sv === 'concluida')           r.concluidas++;
    }
    return r;
  }, [visitas]);

  const grupos = useMemo(() => {
    const g = {};
    for (const t of tecsFiltrados) {
      let k;
      if (agruparPor === 'unidade') {
        k = t.unidade_negocio || '(sem unidade)';
      } else {
        k = setoresPorTec[String(t.id)]?.setor_primario || 'sem_dados';
      }
      (g[k] = g[k] || []).push(t);
    }
    for (const k of Object.keys(g)) {
      g[k].sort((a, b) =>
        (visitasPorTecnico[String(b.id)] || []).length - (visitasPorTecnico[String(a.id)] || []).length
      );
    }
    return g;
  }, [tecsFiltrados, visitasPorTecnico, agruparPor, setoresPorTec]);

  const ordemUnidades = ['Matriz','Serra','Praia','Ilha Sul','Ilha Leste','(sem unidade)'];
  const ordemSetores = ['suporte','ativacao','recolhimento','infraestrutura'];
  const ordemGrupos = agruparPor === 'unidade' ? ordemUnidades : ordemSetores;
  const gruposOrdenados = ordemGrupos.filter(u => grupos[u]?.length);

  function toggleSkill(tid) {
    setFiltroSkills(prev => {
      const n = new Set(prev);
      if (n.has(tid)) n.delete(tid); else n.add(tid);
      return n;
    });
  }
  function toggleGrupo(g) {
    setGruposColapsados(prev => {
      const n = new Set(prev);
      if (n.has(g)) n.delete(g); else n.add(g);
      return n;
    });
  }

  function corDoGrupo(g) {
    return agruparPor === 'unidade' ? (COR_UNIDADE[g] || '#6b7280') : (COR_SETOR[g]?.cor || '#6b7280');
  }
  function labelDoGrupo(g) {
    return agruparPor === 'unidade' ? g : (COR_SETOR[g]?.label || g);
  }

  const isHoje = data === dataSCcompare();
  const visitasExibidas = useMemo(() => {
    const ids = new Set(tecsFiltrados.map(t => String(t.id)));
    return visitas.filter(v => ids.has(String(v.tecnico_id)));
  }, [visitas, tecsFiltrados]);

  const horasArray = Array.from({ length: HORA_FIM - HORA_INICIO + 1 }, (_, i) => HORA_INICIO + i);
  const larguraTimeline = TOTAL_MIN * PX_POR_MIN;

  return (
    <div className="stack">
      {erro && <div className="card error">{erro}</div>}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Agenda</h2>
        <DatalakeStatus />
      </div>



      {/* BANNER DE FERIADO */}
      {feriadosNaData && feriadosNaData.length > 0 && (() => {
        const globais   = feriadosNaData.filter(f => ['nacional','estadual','facultativo','empresa'].includes(f.tipo));
        const municipais = feriadosNaData.filter(f => f.tipo === 'municipal');
        return (
          <>
            {globais.length > 0 && (
              <div style={{
                padding: '0.75rem 1rem',
                background: 'linear-gradient(to right, #fef3c7, #fef9c3)',
                border: '1.5px solid #f59e0b',
                borderRadius: 6,
                display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
              }}>
                <span style={{ fontSize: '1.5rem' }}>🎉</span>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <strong style={{ color: '#92400e' }}>
                    {globais.map(f => f.nome).join(' · ')}
                  </strong>
                  <div style={{ fontSize: '0.8rem', color: '#78350f' }}>
                    Feriado {globais[0].tipo} — afeta toda a equipe.
                    Use a tela <strong>Feriados</strong> pra escalar quem fará plantão.
                  </div>
                </div>
                <button className="btn small" onClick={() => navigate('/feriados')}>
                  Gerenciar plantão
                </button>
              </div>
            )}
            {municipais.map(f => (
              <div key={f.id} style={{
                padding: '0.75rem 1rem',
                background: 'linear-gradient(to right, #dbeafe, #eff6ff)',
                border: '1.5px solid #3b82f6',
                borderRadius: 6,
                display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap',
              }}>
                <span style={{ fontSize: '1.5rem' }}>🏛️</span>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <strong style={{ color: '#1e40af' }}>
                    {f.nome} ({f.cidade})
                  </strong>
                  <div style={{ fontSize: '0.8rem', color: '#1e3a8a', marginTop: 2 }}>
                    Feriado municipal — afeta <strong>{f.afetados_count}</strong> técnico{f.afetados_count !== 1 ? 's' : ''}
                    {f.afetados_count > 0 && f.tecnicos_afetados && (
                      <>: {f.tecnicos_afetados.slice(0, 6).map(t => t.apelido).join(', ')}
                        {f.tecnicos_afetados.length > 6 && ` +${f.tecnicos_afetados.length - 6}`}
                      </>
                    )}
                    . Demais técnicos trabalham normalmente.
                  </div>
                </div>
                <button className="btn small" onClick={() => navigate('/feriados')}>
                  Gerenciar
                </button>
              </div>
            ))}
          </>
        );
      })()}

      {/* KPI Cards */}
      {(() => {
        const totalVis = visitasExibidas.length;
        const totalTec = tecsFiltrados.length;
        const concluidas = visitasExibidas.filter(v => v.status === 'concluida').length;
        const agendadas  = visitasExibidas.filter(v => v.status === 'agendada').length;
        const naoCompareceu = visitasExibidas.filter(v => v.status === 'nao_compareceu').length;
        const pctConcluidas = totalVis > 0 ? Math.round(concluidas * 100 / totalVis) : 0;
        const pctAgendadas  = totalVis > 0 ? Math.round(agendadas  * 100 / totalVis) : 0;
        return (
          <div className="kpi-grid">
            <KPICard
              icon="📅" iconBg="var(--laranja-claro)" iconColor="var(--laranja)"
              label="Visitas do dia" valor={totalVis} total={visitas.length}
              sub={`${pctConcluidas}% realizadas`}
              barColor="var(--laranja)" barPct={pctConcluidas}
            />
            <KPICard
              icon="👥" iconBg="var(--roxo-claro)" iconColor="var(--roxo)"
              label="Técnicos filtrados" valor={totalTec} total={tecs.length}
              sub={`${tecs.length > 0 ? Math.round(totalTec * 100 / tecs.length) : 0}% da equipe`}
              barColor="var(--roxo)" barPct={tecs.length > 0 ? totalTec * 100 / tecs.length : 0}
            />
            <KPICard
              icon="✓" iconBg="var(--verde-claro)" iconColor="var(--verde)"
              label="Concluídas" valor={concluidas}
              sub={totalVis > 0 ? `${pctConcluidas}% do total` : '—'}
            />
            <KPICard
              icon="⏰" iconBg="#FEF3C7" iconColor="#D97706"
              label="Agendadas" valor={agendadas}
              sub={totalVis > 0 ? `${pctAgendadas}% do total` : '—'}
            />
            <KPICard
              icon={naoCompareceu > 0 ? "⚠️" : "📊"}
              iconBg={naoCompareceu > 0 ? 'var(--vermelho-claro)' : '#DBEAFE'}
              iconColor={naoCompareceu > 0 ? 'var(--vermelho)' : 'var(--azul)'}
              label={naoCompareceu > 0 ? 'Não compareceram' : 'Visitas por dia'}
              valor={naoCompareceu > 0 ? naoCompareceu : visitas.length}
              sub={naoCompareceu > 0 ? 'requer atenção' : 'total importadas'}
            />
          </div>
        );
      })()}

      {/* Filtros */}
      <div className="card" style={{ padding: '0.75rem 1rem' }}>
        <div className="stack" style={{ gap: '0.6rem' }}>
          {/* Linha 1: data + toggle de agrupamento + busca */}
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
              <input type="date" value={dataInput}
                onChange={(e) => setDataInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') aplicarData(); }}
                style={{ width: 'auto' }}/>
              <button className="btn small" onClick={aplicarData} disabled={carregando}
                style={{ padding: '0.3rem 0.6rem' }}>▶</button>
              <button className="btn small secondary" onClick={carregarVisitas} disabled={carregando}
                style={{ padding: '0.3rem 0.6rem' }}>{carregando ? '…' : '↻'}</button>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.85rem', cursor: 'pointer' }}
              title="Mostra só técnicos na escala do dia (jornada de trabalho)">
              <input type="checkbox" checked={modoEscala}
                onChange={e => setModoEscala(e.target.checked)}
                style={{ width: 'auto' }}/>
              na escala hoje
            </label>


            {/* Busca de técnico unificada na barra de busca de OS — ver linha ~668 */}

            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}><strong>Agrupar por:</strong></span>
            <div style={{ display: 'flex', gap: '0.2rem' }}>
              <Chip ativo={agruparPor === 'unidade'} onClick={() => setAgruparPor('unidade')}>
                📍 Unidade
              </Chip>
              <Chip ativo={agruparPor === 'setor'} onClick={() => setAgruparPor('setor')}>
                🎯 Setor
              </Chip>
            </div>
          </div>

          {/* Linha 2: filtros de unidade OU setor */}
          {agruparPor === 'unidade' ? (
            <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
              <Chip ativo={filtroUnidade === 'todas'} onClick={() => setFiltroUnidade('todas')}>
                Todas unidades
              </Chip>
              {clustersList.map(c => (
                <Chip key={c.id} ativo={filtroUnidade === c.unidade_negocio} cor={c.cor_hex}
                  onClick={() => setFiltroUnidade(c.unidade_negocio)}>
                  {c.unidade_negocio || c.nome}
                </Chip>
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
              <Chip ativo={filtroSetor === 'todos'} onClick={() => setFiltroSetor('todos')}>
                Todos setores
              </Chip>
              {Object.entries(COR_SETOR).map(([k, info]) => (
                <Chip key={k} ativo={filtroSetor === k} cor={info.cor}
                  onClick={() => setFiltroSetor(k)}>
                  {info.label}
                </Chip>
              ))}
            </div>
          )}

          {/* Linha 3: skills agrupadas por setor (colapsável) */}
          {tipos.length > 0 && (() => {
            // Agrupa por setor
            const porSetor = {};
            for (const t of tipos) {
              const s = t.setor || 'outros';
              if (!porSetor[s]) porSetor[s] = [];
              porSetor[s].push(t);
            }
            // Conta visitas hoje por tipo
            const visitasPorTipo = {};
            for (const lista of Object.values(visitasPorTecnico)) {
              for (const v of lista) {
                if (v.tipo_os) visitasPorTipo[v.tipo_os] = (visitasPorTipo[v.tipo_os] || 0) + 1;
              }
            }
            const ordemSetorSkill = ['suporte','ativacao','recolhimento','infraestrutura','outros'];
            const setoresOrdenados = ordemSetorSkill.filter(s => porSetor[s]);
            return (
              <div style={{
                display: 'flex', flexDirection: 'column', gap: '0.35rem',
                borderTop: '1px solid var(--cinza-borda)', paddingTop: '0.5rem',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}><strong>Skills:</strong></span>
                  {filtroSkills.size > 0 && (
                    <button className="btn small secondary"
                      onClick={() => setFiltroSkills(new Set())}
                      style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem' }}>
                      × limpar ({filtroSkills.size})
                    </button>
                  )}
                  <span className="muted" style={{ fontSize: '0.7rem' }}>
                    Clique em um setor para expandir as skills
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {setoresOrdenados.map(setor => {
                    const skillsDoSetor = porSetor[setor];
                    const corSetor = COR_SETOR[setor]?.cor || '#9ca3af';
                    const labelSetor = COR_SETOR[setor]?.label || `🔹 ${setor}`;
                    const visitasNoSetor = skillsDoSetor.reduce((acc, t) => acc + (visitasPorTipo[t.tipo] || 0), 0);
                    const selecionadasNoSetor = skillsDoSetor.filter(t => filtroSkills.has(t.id)).length;
                    return (
                      <details key={setor} style={{
                        border: '1px solid var(--cinza-borda)',
                        borderRadius: 6, padding: '0.35rem 0.6rem',
                        background: '#fff',
                        borderLeft: `3px solid ${corSetor}`,
                      }}>
                        <summary style={{
                          cursor: 'pointer', fontSize: '0.78rem',
                          display: 'flex', alignItems: 'center', gap: 8,
                          listStyle: 'none', userSelect: 'none',
                        }}>
                          <span style={{ fontWeight: 600 }}>{labelSetor}</span>
                          <span className="muted" style={{ fontSize: '0.7rem' }}>
                            · {skillsDoSetor.length} skill{skillsDoSetor.length !== 1 ? 's' : ''}
                            {visitasNoSetor > 0 && ` · ${visitasNoSetor} visita${visitasNoSetor !== 1 ? 's' : ''} hoje`}
                          </span>
                          {selecionadasNoSetor > 0 && (
                            <span style={{
                              marginLeft: 'auto', fontSize: '0.7rem', fontWeight: 700,
                              background: '#dbeafe', color: '#1e3a8a',
                              padding: '1px 7px', borderRadius: 8,
                            }}>
                              {selecionadasNoSetor} selecionada{selecionadasNoSetor !== 1 ? 's' : ''}
                            </span>
                          )}
                        </summary>
                        <div style={{
                          display: 'flex', flexWrap: 'wrap', gap: '0.3rem',
                          marginTop: '0.5rem', paddingTop: '0.4rem',
                          borderTop: '1px dashed #e5e7eb',
                        }}>
                          {skillsDoSetor.map(t => {
                            const qtd = visitasPorTipo[t.tipo] || 0;
                            const ativo = filtroSkills.has(t.id);
                            return (
                              <label key={t.id} style={{
                                display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                                padding: '0.25rem 0.55rem', borderRadius: 14,
                                background: ativo ? '#dbeafe' : '#f9fafb',
                                border: `1px solid ${ativo ? '#3b82f6' : 'var(--cinza-borda)'}`,
                                fontSize: '0.72rem', cursor: 'pointer',
                              }}>
                                <input type="checkbox" checked={ativo}
                                  onChange={() => toggleSkill(t.id)}
                                  style={{ width: 'auto', margin: 0 }}/>
                                {tipoOsCurto(t.tipo)}
                                {qtd > 0 && (
                                  <span style={{
                                    background: ativo ? '#fff' : '#fff',
                                    border: `1px solid ${ativo ? '#3b82f6' : '#e5e7eb'}`,
                                    color: ativo ? '#1e3a8a' : '#6b7280',
                                    borderRadius: 9, padding: '0 5px',
                                    fontSize: '0.65rem', fontWeight: 700, minWidth: 16,
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                  }}>{qtd}</span>
                                )}
                              </label>
                            );
                          })}
                        </div>
                      </details>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Ordem: Busca à esquerda · Status no meio · 'agora' à direita */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'stretch', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <BuscaOS value={buscaOS} onChange={setBuscaOS} resumo={resumoBuscaOS} />
        </div>
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          <LegendaStatusVisita
            resumo={resumoStatusVisual}
            statusSelecionado={filtroStatusVisual}
            onChange={setFiltroStatusVisual}
            variante="standalone"
          />
        </div>
        {isHoje && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '8px 14px',
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
            fontSize: 12, color: '#4b5563',
            flexShrink: 0,
          }}>
            <span style={{ width: 2, height: 14, background: COR_NOW }}/>
            <span style={{ fontWeight: 600 }}>
              agora ({String(Math.floor(agoraMin/60)).padStart(2,'0')}:{String(agoraMin%60).padStart(2,'0')})
            </span>
          </div>
        )}
      </div>

      {/* GANTT — usa CSS Grid pra garantir sticky funcionando */}
      <div className="card" style={{ padding: 0, overflow: 'auto', position: 'relative', borderRadius: 12, maxHeight: 'calc(100vh - 280px)' }}>
        {gruposOrdenados.length === 0 ? (
          <div className="muted" style={{ padding: '2rem', textAlign: 'center' }}>
            {carregando ? 'Carregando...' :
              visitas.length === 0 ? `Sem visitas agendadas em ${data}.` : 'Nenhum técnico corresponde aos filtros.'}
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: `${LARGURA_TEC}px ${larguraTimeline}px`,
            position: 'relative',
            minWidth: LARGURA_TEC + larguraTimeline,
          }}>
            {/* === HEADER STICKY (linha 1) === */}
            {/* Canto superior esquerdo */}
            <div style={{
              position: 'sticky', top: 0, left: 0, zIndex: 30,
              background: '#f9fafb',
              borderRight: '1px solid var(--cinza-borda)',
              borderBottom: '2px solid var(--cinza-borda)',
              height: 36,
            }}/>
            {/* Cabeçalho de horas */}
            <div style={{
              position: 'sticky', top: 0, zIndex: 20,
              background: '#fff',
              borderBottom: '2px solid var(--cinza-borda)',
              height: 36,
              position: 'sticky',
            }}>
              <div style={{ position: 'relative', height: '100%' }}>
                {horasArray.map((h, i) => (
                  <div key={h} style={{
                    position: 'absolute',
                    left: i * 60 * PX_POR_MIN,
                    top: 0, height: 36,
                    fontSize: '0.72rem',
                    color: '#6b7280',
                    fontWeight: 600,
                    paddingLeft: 6,
                    paddingTop: 8,
                    borderLeft: i === 0 ? 'none' : '1px solid #e5e7eb',
                    boxSizing: 'border-box',
                    width: 60 * PX_POR_MIN,
                  }}>
                    {String(h).padStart(2,'0')}h
                  </div>
                ))}
              </div>
            </div>

            {/* === LINHAS === */}
            {gruposOrdenados.map(grupo => {
              const tecsDoGrupo = grupos[grupo];
              const colapsado = gruposColapsados.has(grupo);
              const cor = corDoGrupo(grupo);
              return (
                <FragmentoGrupo
                  key={grupo}
                  grupo={grupo}
                  label={labelDoGrupo(grupo)}
                  cor={cor}
                  colapsado={colapsado}
                  tecsDoGrupo={tecsDoGrupo}
                  visitasPorTecnico={visitasPorTecnico}
                  onToggle={() => toggleGrupo(grupo)}
                  horasArray={horasArray}
                  filtroSkills={filtroSkills}
                  filtroStatusVisual={filtroStatusVisual}
                  buscaOS={buscaOS}
                  tipos={tipos}
                  setoresPorTec={setoresPorTec}
                  agruparPor={agruparPor}
                  escalaPorTec={escalaPorTec}
                  onClicarTecnico={(tec) => setModalDisponibilidade({ tec, escala: escalaPorTec[String(tec.id)] })}
                  larguraTimeline={larguraTimeline}
                />
              );
            })}

            {/* Linha vermelha vertical "agora" */}
            {isHoje && agoraMin >= HORA_INICIO * 60 && agoraMin <= HORA_FIM * 60 && (
              <LinhaAgora left={LARGURA_TEC + minToPx(agoraMin)} agoraMin={agoraMin}/>
            )}
          </div>
        )}
      </div>

      {/* Modal de disponibilidade */}
      {modalDisponibilidade && (
        <ModalDisponibilidade
          tec={modalDisponibilidade.tec}
          escala={modalDisponibilidade.escala}
          data={data}
          onFechar={() => setModalDisponibilidade(null)}
          onSalvo={async () => {
            setModalDisponibilidade(null);
            await carregarVisitas();
          }}
        />
      )}
    </div>
  );
}

// Renderiza cabeçalho do grupo + linhas dos técnicos. Cada um vira 2 células da grid.
function FragmentoGrupo({
  grupo, label, cor, colapsado, tecsDoGrupo, visitasPorTecnico, onToggle,
  horasArray, filtroSkills, filtroStatusVisual, buscaOS, tipos, setoresPorTec, agruparPor, larguraTimeline,
  escalaPorTec, onClicarTecnico,
}) {
  const totalVis = tecsDoGrupo.reduce((s, t) => s + (visitasPorTecnico[String(t.id)] || []).length, 0);

  return (
    <>
      {/* Header do grupo: nome (sticky left) + faixa de fundo na trilha */}
      <div
        onClick={onToggle}
        style={{
          position: 'sticky', left: 0, zIndex: 5,
          background: `linear-gradient(to right, ${cor}25, ${cor}10)`,
          borderTop: '1px solid var(--cinza-borda)',
          borderBottom: '1px solid var(--cinza-borda)',
          borderLeft: `4px solid ${cor}`,
          borderRight: '1px solid var(--cinza-borda)',
          padding: '0.4rem 0.8rem',
          cursor: 'pointer', userSelect: 'none',
          fontWeight: 600, fontSize: '0.82rem',
          color: cor, display: 'flex', alignItems: 'center', gap: '0.4rem',
        }}>
        <span>{colapsado ? '▶' : '▼'}</span>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      </div>
      <div style={{
        background: `linear-gradient(to right, ${cor}10, transparent 30%)`,
        borderTop: '1px solid var(--cinza-borda)',
        borderBottom: '1px solid var(--cinza-borda)',
        padding: '0.4rem 0.8rem',
        fontSize: '0.75rem', color: '#6b7280',
        display: 'flex', alignItems: 'center',
        whiteSpace: 'nowrap',
      }}>
        {tecsDoGrupo.length} técnico{tecsDoGrupo.length !== 1 ? 's' : ''} · {totalVis} visita{totalVis !== 1 ? 's' : ''}
      </div>

      {/* Linhas dos técnicos */}
      {!colapsado && tecsDoGrupo.map((t, idx) => {
        const vs = visitasPorTecnico[String(t.id)] || [];
        const bgLinha = idx % 2 === 0 ? '#fff' : '#fafbfc';
        const infoSetores = setoresPorTec[String(t.id)];
        return (
          <LinhaTecnico
            key={t.id}
            tec={t}
            visitas={vs}
            bg={bgLinha}
            horasArray={horasArray}
            filtroSkills={filtroSkills}
            filtroStatusVisual={filtroStatusVisual}
            buscaOS={buscaOS}
            tipos={tipos}
            infoSetores={infoSetores}
            agruparPor={agruparPor}
            escalaTec={escalaPorTec[String(t.id)]}
            onClicar={() => onClicarTecnico(t)}
          />
        );
      })}
    </>
  );
}

function LinhaTecnico({ tec: t, visitas: vs, bg, horasArray, filtroSkills, filtroStatusVisual, buscaOS, tipos, infoSetores, agruparPor, escalaTec, onClicar }) {
  // Calcula lanes (pistas verticais) pra evitar sobreposição de visitas
  const { laneByVisitaId, totalLanes } = useMemo(() => calcularLanes(vs), [vs]);
  const alt = alturaLinha(totalLanes);

  // Constrói barra de proporção multi-setor (apenas setores >=5%)
  const segmentos = [];
  if (infoSetores) {
    const pcts = [
      { key: 'suporte',        pct: infoSetores.pct_suporte || 0 },
      { key: 'ativacao',       pct: infoSetores.pct_ativacao || 0 },
      { key: 'recolhimento',   pct: infoSetores.pct_recolhimento || 0 },
      { key: 'infraestrutura', pct: infoSetores.pct_infraestrutura || 0 },
    ].filter(x => x.pct >= 5);
    pcts.sort((a, b) => b.pct - a.pct);
    for (const s of pcts) segmentos.push(s);
  }
  const tooltipSetores = segmentos.length
    ? segmentos.map(s => `${COR_SETOR[s.key]?.label || s.key}: ${s.pct}%`).join('\n')
      + `\n(últimos 60 dias · ${infoSetores.total} OS)`
    : 'Sem visitas finalizadas em 60 dias';

  return (
    <>
      {/* Nome (sticky LEFT) */}
      <div style={{
        position: 'sticky', left: 0, zIndex: 4,
        background: bg,
        borderRight: '1px solid var(--cinza-borda)',
        borderBottom: '1px solid #e5e7eb',
        padding: '4px 8px',
        display: 'flex', alignItems: 'center',
        height: alt,
        fontSize: '0.78rem',
        overflow: 'hidden',
        gap: 8,
      }}>
        <Avatar nome={t.apelido || t.nome} id={t.id} fotoUrl={t.foto_url} size={32}/>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2, flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, lineHeight: '1.05' }}>
          <button onClick={onClicar} type="button" title="Marcar falta, atraso, saída antecipada"
            style={{
              background: 'none', border: 'none', padding: 0, margin: 0,
              cursor: 'pointer', textAlign: 'left', flex: 1,
              fontFamily: 'inherit', fontSize: 'inherit',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              fontWeight: 700,
              color: escalaTec?.status === 'ausente_dia' ? '#dc2626'
                   : escalaTec?.status === 'jornada_reduzida' ? '#d97706'
                   : escalaTec?.status === 'atrasado' ? '#d97706'
                   : escalaTec?.status === 'feriado_folga' ? '#9ca3af'
                   : escalaTec?.status === 'plantao_feriado' ? '#16a34a'
                   : 'inherit',
            }}>
            {t.apelido || t.nome.split(' ')[0]}
          </button>
          <Link to={`/tecnicos/${t.id}`} title="Editar técnico"
            style={{
              color: '#9ca3af', textDecoration: 'none', fontSize: '0.7rem',
              padding: '0 3px', borderRadius: 3, flexShrink: 0,
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#F15D22'}
            onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}>
            ✏️
          </Link>
          {escalaTec?.status === 'ausente_dia' && <span title={escalaTec.motivo || 'Faltou'} style={{ fontSize: '0.6rem', fontWeight: 700, background: '#fee2e2', color: '#991b1b', padding: '0px 4px', borderRadius: 3, flexShrink: 0 }}>FALTOU</span>}
          {escalaTec?.status === 'jornada_reduzida' && <span title={escalaTec.motivo || 'Jornada reduzida'} style={{ fontSize: '0.6rem', fontWeight: 700, background: '#fef3c7', color: '#92400e', padding: '0px 4px', borderRadius: 3, flexShrink: 0 }}>REDUZ</span>}
          {escalaTec?.status === 'atrasado' && <span title={escalaTec.motivo || 'Atrasado'} style={{ fontSize: '0.6rem', fontWeight: 700, background: '#fef3c7', color: '#92400e', padding: '0px 4px', borderRadius: 3, flexShrink: 0 }}>ATR</span>}
          {escalaTec?.status === 'plantao_feriado' && <span title="Em plantão no feriado" style={{ fontSize: '0.6rem', fontWeight: 700, background: '#dcfce7', color: '#14532d', padding: '0px 4px', borderRadius: 3, flexShrink: 0 }}>🎉 PLANTÃO</span>}
          {escalaTec?.status === 'feriado_folga' && <span title="Folga de feriado" style={{ fontSize: '0.6rem', fontWeight: 700, background: '#f3f4f6', color: '#6b7280', padding: '0px 4px', borderRadius: 3, flexShrink: 0 }}>FOLGA</span>}
          {t.is_terceirizado && (
            <span title="Terceirizado / PJ" style={{
              fontSize: '0.6rem', fontWeight: 700,
              background: '#fef3c7', color: '#92400e',
              padding: '0px 4px', borderRadius: 3,
              flexShrink: 0,
            }}>PJ</span>
          )}
        </div>

        {/* Barrinha multi-setor */}
        {segmentos.length > 0 ? (
          <div title={tooltipSetores} style={{
            display: 'flex', gap: 1,
            height: 5, width: '100%',
            borderRadius: 2, overflow: 'hidden',
            background: '#f3f4f6',
            cursor: 'help',
          }}>
            {segmentos.map(s => (
              <div key={s.key} style={{
                width: `${s.pct}%`,
                background: COR_SETOR[s.key]?.cor || '#9ca3af',
              }}/>
            ))}
          </div>
        ) : (
          <div style={{ height: 5 }}/>
        )}

        <div className="muted" style={{ fontSize: '0.65rem', lineHeight: '1.0' }}>
          {vs.length} visita{vs.length !== 1 ? 's' : ''}
          {infoSetores && ` · ${infoSetores.total} OS/60d`}
        </div>
        </div>
      </div>

      {/* Trilha de visitas */}
      <div style={{
        position: 'relative',
        background: bg,
        borderBottom: '1px solid #e5e7eb',
        height: alt,
      }}>
        {/* CAMADA DE JORNADA — faixa cinza com bordas verticais */}
        <CamadaJornada escalaTec={escalaTec} altLinha={alt}/>

        {horasArray.map((h, i) => (
          i === 0 ? null : (
            <div key={h} style={{
              position: 'absolute',
              left: i * 60 * PX_POR_MIN, top: 0, bottom: 0, width: 1,
              background: '#f1f5f9',
            }}/>
          )
        ))}
        {vs.map(v => <BlocoVisitaH key={v.id} visita={v} filtroSkills={filtroSkills} filtroStatusVisual={filtroStatusVisual} buscaOS={buscaOS} tipos={tipos} lane={laneByVisitaId.get(v.id) || 0} totalLanes={totalLanes}/>)}
      </div>
    </>
  );
}

function LinhaAgora({ left, agoraMin }) {
  return (
    <div style={{
      position: 'absolute',
      top: 36, bottom: 0,
      left, width: 2,
      background: COR_NOW, zIndex: 6,
      pointerEvents: 'none',
      boxShadow: '0 0 8px rgba(220,38,38,0.4)',
    }}>
      <div style={{
        position: 'absolute', top: -18, left: -22,
        background: COR_NOW, color: '#fff',
        padding: '1px 6px', borderRadius: 4,
        fontSize: '0.65rem', fontWeight: 700,
        whiteSpace: 'nowrap',
      }}>
        {String(Math.floor(agoraMin/60)).padStart(2,'0')}:{String(agoraMin%60).padStart(2,'0')}
      </div>
    </div>
  );
}

function BlocoVisitaH({ visita: v, filtroSkills, filtroStatusVisual, buscaOS, tipos, lane = 0, totalLanes = 1 }) {
  const statusVisual = classificarVisita(v);
  let cor = COR_STATUS_VISUAL[statusVisual] || COR_STATUS_VISUAL.agendada;
  // OS internas (MAIS TELECOM) com cor distinta (roxo)
  if (v.is_interno) {
    cor = { borda: '#7c3aed', fundo: '#ede9fe', texto: '#4c1d95' };
  }

  const iniAg = horaSCparaMin(v.janela_inicio);
  const fimAg = horaSCparaMin(v.janela_fim);
  const iniReal = horaSCparaMin(v.dt_inicio_atendimento);
  const fimReal = horaSCparaMin(v.dt_fechamento);

  if (iniAg == null || fimAg == null) return null;
  if (fimAg < HORA_INICIO * 60 || iniAg > HORA_FIM * 60) return null;

  // Filtro de skill — esmaece ou destaca
  let destacar = false, esmaecer = false;
  if (filtroSkills && filtroSkills.size > 0) {
    const tipoEh = tipos.find(t => t.tipo === v.tipo_os);
    if (tipoEh && filtroSkills.has(tipoEh.id)) destacar = true;
    else esmaecer = true;
  }
  // Filtro de status visual — esmaece blocos fora do filtro
  if (filtroStatusVisual && statusVisual !== filtroStatusVisual) {
    esmaecer = true;
  }
  // Filtro de busca por OS — esmaece blocos cujo protocolo/cliente não casa
  if (buscaOS && buscaOS.trim() && !visitaCasaBusca(v, buscaOS)) {
    esmaecer = true;
  }

  // Posição vertical: top determinado pela lane
  const topLane = PADDING_V + lane * (ALT_LANE + GAP_LANES);
  // Quando só 1 lane, usa altura cheia (mais informação visível)
  // Quando múltiplas, fica mais compacto pra caber sobreposições
  const altBloco = ALT_LANE;
  // Divisão: previsto em cima, realizado em baixo (se houver)
  const temRealizado = (iniReal != null) || (fimReal != null);
  const altPrev = temRealizado ? Math.round(altBloco * 0.62) : altBloco;
  const altReal = altBloco - altPrev - 1; // 1px de "gap" entre eles (borda inferior do previsto)

  // === PREVISTO ===
  const leftAg  = minToPx(iniAg);
  const widthAg = Math.max(20, (fimAg - iniAg) * PX_POR_MIN);

  // === REALIZADO ===
  let leftReal = null, widthReal = null, foraDaJanela = false;
  let labelReal = null, deltaMin = null;
  if (iniReal != null && fimReal != null) {
    leftReal  = minToPx(iniReal);
    widthReal = Math.max(8, (fimReal - iniReal) * PX_POR_MIN);
    foraDaJanela = (fimReal < iniAg - 15) || (iniReal > fimAg + 15);
    deltaMin = iniReal - iniAg;
    labelReal = `${formatHoraSC(v.dt_inicio_atendimento)} → ${formatHoraSC(v.dt_fechamento)}`;
  } else if (iniReal != null) {
    // Em andamento (sem fim)
    const agora = nowMinSC();
    leftReal  = minToPx(iniReal);
    widthReal = Math.max(8, (Math.max(agora, fimAg) - iniReal) * PX_POR_MIN);
    deltaMin = iniReal - iniAg;
    labelReal = `${formatHoraSC(v.dt_inicio_atendimento)} → em andamento`;
    foraDaJanela = Math.abs(deltaMin) > 15;
  } else if (fimReal != null) {
    // Só fim registrado (raro)
    leftReal  = minToPx(fimReal) - 6;
    widthReal = 12;
    labelReal = `finalizado ${formatHoraSC(v.dt_fechamento)}`;
    foraDaJanela = (fimReal < iniAg - 15) || (fimReal > fimAg + 15);
  }

  // Label de delta legível
  let deltaLabel = '';
  if (deltaMin != null) {
    if (Math.abs(deltaMin) < 5) deltaLabel = 'no horário';
    else if (deltaMin > 0) deltaLabel = `+${Math.round(deltaMin)}min`;
    else deltaLabel = `${Math.round(deltaMin)}min`;
  }

  const tooltip = [
    `${osLabel(v)} — ${v.cliente_nome || ''}`,
    `Tipo: ${v.tipo_os || ''}`,
    `Previsto: ${formatHoraSC(v.janela_inicio)}–${formatHoraSC(v.janela_fim)}`,
    iniReal != null ? `Iniciado: ${formatHoraSC(v.dt_inicio_atendimento)} (${deltaLabel})` : 'Iniciado: não registrado',
    fimReal != null ? `Finalizado: ${formatHoraSC(v.dt_fechamento)}` : 'Finalizado: em aberto',
    `Status: ${v.status}`,
    foraDaJanela ? '⚠ Fora da janela contratada' : '',
  ].filter(Boolean).join('\n');

  // Cor escura do realizado — agora reflete PONTUALIDADE do início:
  //   azul  = iniciou no horário (até +10min)
  //   laranja = atrasou (>10min)
  // Visitas em status atrasada/concluida_atrasada usam o sub-status visual.
  const corReal       = corRealizado(v);
  const corRealFundo  = corReal.borda;
  const corRealTexto  = '#fff';

  // === Conector bezier previsto→realizado (quando deslocados) ===
  // Aparece só quando o realizado começa em horário visivelmente diferente do previsto
  const gapHorizontal = (temRealizado && leftReal != null) ? Math.abs(leftReal - leftAg) : 0;
  const mostraConector = temRealizado && leftReal != null && gapHorizontal > 15 && !esmaecer;

  let conectorSvg = null;
  if (mostraConector) {
    // P1 = canto do previsto que aponta pro realizado
    // P2 = canto do realizado que vem do previsto
    const realDireita = leftReal > leftAg;
    const p1AbsX = realDireita ? leftAg + widthAg : leftAg;
    const p2AbsX = realDireita ? leftReal           : leftReal + widthReal;
    const p1AbsY = topLane + altPrev * 0.5;
    const p2AbsY = topLane + altPrev + altReal * 0.5;

    const svgLeft = Math.min(p1AbsX, p2AbsX) - 2;
    const svgTop  = topLane + altPrev * 0.4;
    const svgW    = Math.abs(p2AbsX - p1AbsX) + 4;
    const svgH    = altReal + altPrev * 0.5;

    const p1x = p1AbsX - svgLeft;
    const p1y = p1AbsY - svgTop;
    const p2x = p2AbsX - svgLeft;
    const p2y = p2AbsY - svgTop;

    // Cubic bezier com control points horizontais (S suave)
    const path = `M ${p1x} ${p1y} C ${p1x} ${p2y}, ${p2x} ${p1y}, ${p2x} ${p2y}`;

    conectorSvg = (
      <svg style={{
        position: 'absolute',
        left: svgLeft, top: svgTop,
        width: svgW, height: svgH,
        pointerEvents: 'none',
        zIndex: 0, overflow: 'visible',
      }}>
        <path
          d={path}
          stroke="#F15D22"
          strokeWidth="1.8"
          fill="none"
          opacity="0.65"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  return (
    <Link to={`/visitas/${v.id}`} title={tooltip} style={{ textDecoration: 'none' }}>
      {/* Conector bezier previsto→realizado (renderizado primeiro pra ficar embaixo) */}
      {conectorSvg}

      {/* === PREVISTO (caixa pontilhada, em cima) === */}
      <div style={{
        position: 'absolute',
        left: leftAg, width: widthAg,
        top: topLane, height: altPrev,
        background: `repeating-linear-gradient(45deg, ${cor.fundo}, ${cor.fundo} 5px, #ffffff80 5px, #ffffff80 7px)`,
        border: destacar ? '2px solid #f59e0b' : `1.5px dashed ${cor.borda}`,
        borderRadius: temRealizado ? '6px 6px 0 0' : '6px',
        padding: '2px 6px',
        fontSize: '0.65rem', color: cor.texto,
        overflow: 'hidden', cursor: 'pointer',
        opacity: esmaecer ? 0.35 : 1,
        boxShadow: destacar ? '0 0 0 2px #fde68a' : 'none',
        zIndex: destacar ? 3 : 1,
        boxSizing: 'border-box',
      }}>
        <div style={{ fontWeight: 700, lineHeight: '1.1', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {osLabel(v)} {v.is_interno ? '🏗️ INFRA' : (v.cliente_nome || '(sem cliente)')}
        </div>
        {altPrev > 18 && (
          <div style={{ lineHeight: '1.0', fontSize: '0.6rem', opacity: 0.75, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {tipoOsCurto(v.tipo_os)}
          </div>
        )}
      </div>

      {/* === REALIZADO (barra colorida embaixo) === */}
      {temRealizado && leftReal != null && (
        <div style={{
          position: 'absolute',
          left: leftReal, width: widthReal,
          top: topLane + altPrev, height: altReal,
          background: corRealFundo,
          border: foraDaJanela ? `1.5px solid #111` : 'none',
          borderRadius: '0 0 6px 6px',
          color: corRealTexto,
          fontSize: '0.6rem', fontWeight: 600,
          display: 'flex', alignItems: 'center',
          padding: '0 5px',
          whiteSpace: 'nowrap', overflow: 'hidden',
          opacity: esmaecer ? 0.35 : (foraDaJanela ? 1 : 0.92),
          zIndex: 2,
          boxSizing: 'border-box',
          cursor: 'pointer',
        }}>
          ▶ {labelReal}{deltaLabel && deltaLabel !== 'no horário' ? ` (${deltaLabel})` : ''}
        </div>
      )}
    </Link>
  );
}



// ─── CAMADA DE JORNADA — faixa cinza + bordas verticais ─────────
function CamadaJornada({ escalaTec, altLinha = ALT_LINHA_MIN }) {
  if (!escalaTec) return null;
  const ausente = escalaTec.status === 'ausente_dia';
  const plantao = escalaTec.status === 'plantao_feriado';
  const folga   = escalaTec.status === 'feriado_folga';
  const naEscala = escalaTec.na_escala_padrao;

  if (folga) return null; // feriado folga: não desenha faixa
  if (!naEscala && !plantao && !escalaTec.inicio_efetivo) return null;

  const ini = horaStrParaMin(escalaTec.inicio_efetivo);
  const fim = horaStrParaMin(escalaTec.fim_efetivo);
  if (ini == null || fim == null) return null;

  const left = Math.max(0, minToPx(ini));
  const right = Math.min(TOTAL_MIN * PX_POR_MIN, minToPx(fim));
  const width = right - left;

  const pausaIni = horaStrParaMin(escalaTec.pausa_almoco_inicio);
  const pausaFim = horaStrParaMin(escalaTec.pausa_almoco_fim);

  const corFundo = ausente ? '#fee2e2' : (plantao ? '#dcfce7' : '#f3f4f680');
  const corBorda = ausente ? '#fca5a5' : (plantao ? '#86efac' : '#cbd5e1');

  return (
    <>
      {/* Faixa principal (jornada efetiva) */}
      <div style={{
        position: 'absolute',
        top: 0, bottom: 0,
        left, width,
        background: ausente
          ? 'repeating-linear-gradient(45deg, #fee2e2, #fee2e2 6px, #fecaca 6px, #fecaca 8px)'
          : corFundo,
        zIndex: 0,
        pointerEvents: 'none',
      }}/>
      {/* Borda esquerda (início) */}
      <div style={{
        position: 'absolute',
        top: 0, bottom: 0,
        left, width: 2,
        background: corBorda,
        zIndex: 0,
        pointerEvents: 'none',
      }}/>
      {/* Borda direita (fim) */}
      <div style={{
        position: 'absolute',
        top: 0, bottom: 0,
        left: right - 2, width: 2,
        background: corBorda,
        zIndex: 0,
        pointerEvents: 'none',
      }}/>
      {/* Pausa almoço */}
      {pausaIni != null && pausaFim != null && (
        <div style={{
          position: 'absolute',
          top: 0, bottom: 0,
          left: minToPx(pausaIni),
          width: (pausaFim - pausaIni) * PX_POR_MIN,
          background: 'repeating-linear-gradient(135deg, #ffffff80, #ffffff80 3px, #e5e7eb80 3px, #e5e7eb80 6px)',
          zIndex: 0,
          pointerEvents: 'none',
        }}/>
      )}
      {/* Texto AUSENTE centralizado se faltou */}
      {ausente && width > 80 && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: left + width / 2,
          transform: 'translate(-50%, -50%)',
          fontSize: '0.7rem',
          fontWeight: 700,
          color: '#991b1b',
          background: '#fff',
          padding: '1px 6px',
          borderRadius: 3,
          border: '1px solid #fca5a5',
          zIndex: 1,
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
        }}>FALTOU</div>
      )}
    </>
  );
}

function horaStrParaMin(hhmm) {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return null;
  return h * 60 + m;
}

// ─── MODAL DE DISPONIBILIDADE ──────────────────────────────────
function ModalDisponibilidade({ tec, escala: e, data, onFechar, onSalvo }) {
  const [aba, setAba] = useState('atalho'); // 'atalho' ou 'manual'
  const [horaCustom, setHoraCustom] = useState('');
  const [motivo, setMotivo] = useState(e?.motivo || '');
  const [salvando, setSalvando] = useState(false);

  async function aplicar(status, opts = {}) {
    setSalvando(true);
    try {
      await escala.override({
        tecnico_id: tec.id,
        data,
        status,
        inicio_efetivo: opts.inicio || null,
        fim_efetivo:    opts.fim || null,
        motivo:         opts.motivo || motivo || null,
      });
      onSalvo();
    } catch (err) {
      alert('Erro: ' + (err.uiMessage || err.message));
    } finally { setSalvando(false); }
  }

  const isHoje = data === new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
  const agora = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });

  const statusAtual = e?.status || (e?.na_escala_padrao ? 'normal' : 'fora_escala');
  const jornada = e ? `${(e.jornada_inicio_padrao || '').slice(0,5)} – ${(e.jornada_fim_padrao || '').slice(0,5)}` : '?';
  const jornadaEfetiva = e ? `${(e.inicio_efetivo || '').slice(0,5)} – ${(e.fim_efetivo || '').slice(0,5)}` : '?';

  return (
    <div onClick={onFechar} style={{
      position: 'fixed', inset: 0, background: '#0008',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div onClick={ev => ev.stopPropagation()} style={{
        background: '#fff', borderRadius: 8,
        padding: '1.5rem', minWidth: 380, maxWidth: 500,
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>Disponibilidade do dia</h3>
          <button onClick={onFechar} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280' }}>×</button>
        </div>

        <div style={{ fontSize: '0.85rem', color: '#374151' }}>
          <strong>{tec.apelido || tec.nome}</strong> · {data.split('-').reverse().join('/')}
        </div>
        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '1rem' }}>
          Jornada padrão: <strong>{jornada}</strong>
          {statusAtual !== 'normal' && statusAtual !== 'fora_escala' && (
            <> · <span style={{ color: '#d97706', fontWeight: 600 }}>
              Efetivo hoje: {jornadaEfetiva} ({statusAtual.replace('_', ' ')})
            </span></>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <button className="btn" onClick={() => aplicar('ausente_dia')} disabled={salvando}
            style={{ background: '#dc2626' }}>
            🚫 Faltou o dia todo
          </button>

          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <input type="time" value={horaCustom} onChange={ev => setHoraCustom(ev.target.value)}
              placeholder={isHoje ? agora : ''} style={{ width: 100 }}/>
            <button className="btn small secondary" disabled={salvando || !horaCustom}
              onClick={() => aplicar('atrasado', { inicio: horaCustom })}>
              ⏰ Começa às {horaCustom || '...'} (atrasado)
            </button>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <input type="time" value={horaCustom} onChange={ev => setHoraCustom(ev.target.value)}
              placeholder={isHoje ? agora : ''} style={{ width: 100 }}/>
            <button className="btn small secondary" disabled={salvando || !horaCustom}
              onClick={() => aplicar('jornada_reduzida', { fim: horaCustom })}>
              🏃 Sai às {horaCustom || '...'} (sai mais cedo)
            </button>
          </div>

          <textarea value={motivo} onChange={ev => setMotivo(ev.target.value)}
            placeholder="Motivo (opcional)..." rows={2}
            style={{ width: '100%', fontFamily: 'inherit', resize: 'vertical' }}/>

          {statusAtual !== 'normal' && statusAtual !== 'fora_escala' && (
            <button className="btn secondary" onClick={() => aplicar('normal')} disabled={salvando}
              style={{ marginTop: '0.5rem' }}>
              🟢 Voltou ao normal (limpar override)
            </button>
          )}
        </div>

        <div className="muted" style={{ fontSize: '0.7rem', marginTop: '1rem', borderTop: '1px solid var(--cinza-borda)', paddingTop: '0.5rem' }}>
          ⚡ Quando você marcar falta ou jornada reduzida, a IA vai analisar a agenda e propor realocações automaticamente em ~5 minutos. Acompanhe em <strong>Ações IA</strong>.
        </div>
      </div>
    </div>
  );
}

function Chip({ children, ativo, cor, onClick }) {
  const customCor = cor && ativo;
  return (
    <button onClick={onClick}
      className={`chip-base${ativo ? ' active' : ''}`}
      style={customCor ? {
        background: cor,
        borderColor: cor,
        color: '#fff',
      } : undefined}>
      {children}
    </button>
  );
}
